import { Component, createRef, Fragment } from 'react'
import { APP_LANGUAGE } from '../../localized-urls'
import { FormattedMessage } from 'react-intl'
import find from 'lodash/find'
import reject from 'lodash/reject'
import SelectedMethod from './SelectedMethod'
import { CN_CODE } from 'utils'

export default class PaymentMethods extends Component {
  constructor(props) {
    super(props)
    this.stripeInstance = null
    this.stripeElements = null
    this.card = null
    this.iban = null
    this.errorsContainer = createRef()
    this.createSource = this.createSource.bind(this)
    this.state = {
      selectedMethod: null,
      paymentMethodsList: [
        {
          'value': 'visa',
          'label': 'Visa',
          'flow': 'card',
          'internal_value': 'card',
        },
        {
          'value': 'mastercard',
          'label': 'Mastercard',
          'flow': 'card',
          'internal_value': 'card',
        },
        {
          'value': 'american_express',
          'label': 'American Express',
          'flow': 'card',
          'internal_value': 'card',
        },
        {
          'value': 'wechat',
          'label': 'Wechat',
          'flow': 'card',
          'internal_value': 'card',
        },
      ],
    }
  }

  componentDidMount() {
    const { paymentMethodsList } = this.state
    const { preselectedMethod } = this.props
    if(preselectedMethod) {
      this.onPaymentMethodChange(
        preselectedMethod,
        find(paymentMethodsList, { value: preselectedMethod }),
      )
    }
  }

  init(onLoad) {
    if(window.stripe) {
      return false
    }
    const script = document.createElement('script')

    script.type = 'text/javascript'
    script.src = 'https://js.stripe.com/v3/'
    script.onload = () => {
      // TO DO: CHANGE TO INFO
      this.stripeInstance = window.Stripe(window.info.stripePublicKey)
      this.stripeElements = this.stripeInstance.elements({
        locale: APP_LANGUAGE,
        fonts: [{ cssSrc: '/css/stripe-fonts.css' }],
      })

      if(onLoad) {
        onLoad()
      }
    }
    document.body.appendChild(script)
  }

  createPaymentForm = () => {
    const stripeErrors = this.errorsContainer.current
    if(stripeErrors) {
      stripeErrors.classList.remove('visible')
    }

    if(this.card) {
      return true
    }
    this.card = this.stripeElements.create('card', {
      iconStyle: 'solid',
      hidePostalCode: true,
      style: this.defaultStyle,
    })

    this.card.mount('#card-number')

    this.card.on('change', (event) => {
      this.setErrors(event.error)
    })
  }

  createIbanForm = () => {
    // Create an instance of the iban Element.
    const stripeErrors = this.errorsContainer.current
    stripeErrors.classList.remove('visible')
    if(this.iban) {
      return true
    }
    this.iban = this.stripeElements.create('iban', {
      style: this.defaultStyle,
      supportedCountries: ['SEPA'],
    })
    this.bankName = document.getElementById('bank-name')

    // Add an instance of the iban Element into the `iban-element` <div>.
    this.iban.mount('#iban-element')

    this.iban.on('change', (event) => {
      // Handle real-time validation errors from the iban Element.
      this.setErrors(event.error)
      // Display bank name corresponding to IBAN, if available.
      if(event.bankName) {
        this.bankName.innerText = event.bankName
        this.bankName.classList.add('visible')
      } else {
        this.bankName.classList.remove('visible')
      }
    })
  }

  createIdealForm = () => {
    const stripeErrors = this.errorsContainer.current
    stripeErrors.classList.remove('visible')

    if(this.ideal) {
      return true
    }
    this.ideal = this.stripeElements.create('idealBank', {
      style: {
        base: {
          fontSize: '16px',
          padding: '10px',
        },
      },
    })

    this.ideal.mount('#ideal-bank-element')
  }

  createSource(values) {
    if(!this.stripeInstance) {
      // eslint-disable-next-line prefer-promise-reject-errors
      return new Promise((resolve, reject) => reject('No form'))
    }
    const { paymentFlow } = this.state
    if(paymentFlow === 'sepa') {
      return this.createSourceFromIban(values)
    } else if(paymentFlow === 'redirect') {
      return this.submitRedirectForm(values)
    }
    return this.stripeInstance.createSource(this.card, { owner: { name: values?.nameOnCard } }).then((result) => {
      this.setErrors(result.error)
      return result
    })
  }

  createSourceFromIban(data) {
    const sourceData = {
      type: 'sepa_debit',
      currency: 'eur',
      owner: {
        name: data.ibanName,
        email: data.ibanEmail,
      },
      mandate: {
        // Automatically send a mandate notification email to your customer
        // once the source is charged.
        notification_method: 'email',
      },
    }

    // Call `stripe.createSource` with the iban Element and additional options.
    return this.stripeInstance
      .createSource(this.iban, sourceData)
      .then((result) => {
        this.setErrors(result.error)
        return result
      })
  }

  submitRedirectForm(data) {
    const { selectedMethod } = this.state
    const { chargeInfo } = this.props
    var redirectData = {
      type: selectedMethod,
      amount: chargeInfo.amountToCharge,
      currency: 'eur',
      statement_descriptor: chargeInfo.statementDescriptor,
      owner: {
        name: data.redirectName,
      },
      // Specify the URL to which the customer should be redirected
      // after paying.
      redirect: {
        return_url: `${window.location.href}?paymentComplete=true`,
      },
    }
    if(this.ideal) {
      return this.stripeInstance
        .createSource(this.ideal, redirectData)
        .then((result) => {
          this.setErrors(result.error)
          return result
        })
    }
    return this.stripeInstance.createSource(redirectData).then((result) => {
      this.setErrors(result.error)
      return result
    })
  }

  setErrors(error) {
    const stripeErrors = this.errorsContainer.current
    if(error) {
      // Inform the customer that there was an error.
      stripeErrors.textContent = error.message
      stripeErrors.classList.add('visible')
    } else {
      // Send the Source to your server to create a charge.
      stripeErrors.classList.remove('visible')
    }
  }

  cardDestroy() {
    if(!this.card && !this.ideal && !this.iban) {
      return false
    }
    this.card && this.card.destroy()
    this.card = null
    this.ideal && this.ideal.destroy()
    this.ideal = null
    this.iban && this.iban.destroy()
    this.iban = null
  }

  onPaymentMethodChange = (value, paymentMethod) => {
    this.cardDestroy()
    this.setState(
      {
        selectedMethod: value,
        paymentFlow: paymentMethod.flow,
      },
      () => {
        if(paymentMethod.flow === 'sepa') {
          this.init(this.createIbanForm)
        } else if(this.state.selectedMethod === 'ideal') {
          this.init(this.createIdealForm)
        } else {
          this.init(this.createPaymentForm)
        }

        if(this.props.setSelectedMethod) {
          this.props.setSelectedMethod(paymentMethod.value)
        }
      },
    )
  }

  getPaymentMethodsList = () => {
    const { paymentMethodsList } = this.state
    const { shop } = this.props
    if(shop?.countryCode === CN_CODE) {
      return paymentMethodsList.filter(method => method.value === 'wechat')
    } else {
      return paymentMethodsList.filter(method => method.value !== 'wechat')
    }
  }

  render() {
    const {
      paymentMethodsList,
      selectedMethod,
    } = this.state
    const {
      isLoading,
      unconfirmedOrder,
      successMessage,
      isSuccess,
      ccFormButtonText,
    } = this.props

    return (
      <div className='payment-methods-list adventure-checkout'>
        <div className='payment-methods-list__head'>
          {this.props.sectionTitle || (
            <FormattedMessage
              id='payment-method'
              defaultMessage='Payment method'
              tagName='strong'
            />
          )}
        </div>
        {!isSuccess ? (
          <Fragment>
            {this.getPaymentMethodsList().map((paymentMethod) => (
              <Fragment key={paymentMethod.value}>
                <div className='payment-methods-list__item'>
                  <div className='radio-wrap'>
                    <label>
                      <input
                        type='radio'
                        checked={selectedMethod === paymentMethod.value}
                        onChange={(e) =>
                          this.onPaymentMethodChange(
                            e.target.value,
                            paymentMethod,
                          )
                        }
                        value={paymentMethod.value}
                        name='orderPaymentType'
                      />
                      <span>{paymentMethod.label}</span>
                    </label>
                  </div>
                  {paymentMethod.value === 'wechat'
                    ? <div className='pm-icon pm-icon-wechat' />
                    : <div className={`pm-icon pm-icon-${paymentMethod.value}`} />}
                </div>
                {selectedMethod === paymentMethod.value && paymentMethod.value !== 'wechat' && (
                  <SelectedMethod
                    unconfirmedOrder={unconfirmedOrder}
                    ccFormButtonText={ccFormButtonText}
                    flow={paymentMethod.flow}
                    currentMethod={paymentMethod.value}
                    errorsRef={this.errorsContainer}
                    isLoading={isLoading}
                    isAdventure={this.props.isAdventure}
                  />
                )}
              </Fragment>
            ))}
          </Fragment>
        ) : (
          <div className='payment-success'>
            <FormattedMessage
              id='thank_you_exclamation'
              defaultMessage='Thank you!'
              tagName='h4'
            />
            <p>
              {successMessage || (
                <FormattedMessage
                  id='payment_complete'
                  defaultMessage='Your payment is complete'
                />
              )}
            </p>
          </div>
        )}
      </div>
    )
  }
}
